<template>
  <div v-if="!tapStore.view || route.path.includes('/analytics')">
    <div class="navigation d-flex align-center" :class="{'no-round': languages?.length > 1}">
      <v-hover>
        <template #default="{ isHovering, props }">
          <router-link to="/profile" v-bind="props" class="navigation__back">
            <v-icon :icon="BackNewIcon" :color="isHovering ? 'primary' : 'accent'" size="24" class="cursor-pointer" @click="tapStore.setTap(null)"/>
          </router-link>
        </template>
      </v-hover>
      <button v-if="isEditMode" type="button" class="tap-selector" @click="openInnerTapList">
        <span class="tap-name">{{ name }}</span>
        <v-icon :icon="ChevronDownNewIcon" size="24" class="tap-selector__icon"/>
      </button>
      <div v-else class="tap-name">{{ name }}</div>
      <v-hover v-if="isEditMode">
        <template #default="{ isHovering, props }">
          <button
              v-bind="props"
              type="button"
              class="navigation__menu"
              :aria-label="$t('Меню')"
              @click="openContext"
          >
            <v-icon :icon="UnionIcon" :color="isHovering ? 'primary' : 'BW-500'" size="24"/>
          </button>
        </template>
      </v-hover>
      <div v-else class="navigation__spacer" aria-hidden="true"></div>
    </div>
    <div class="d-flex languages" v-if="languages?.length > 1" :class="{'highlighted-element': showOnboarding}">
      <div v-for="lang in languages" :key="lang.orderIndex" class="tab pt-2 pb-4"
           :class="{'active': lang.lang == tapStore.currentTapLang?.lang }" @click="setLang(lang.lang)">
        {{$t(lang.lang)}}
      </div>
      <v-menu
          :model-value="showOnboarding"
          location="bottom"
          z-index="2222"
          activator="parent"
          offset="4"
          :max-width="'100%'"
          :close-on-content-click="false"
          transition="fade-transition"
          @update:model-value="sentHint"
      >
        <div class="d-flex justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="10" viewBox="0 0 40 10" fill="none">
            <path d="M19.9508 0L16.9419 2.99887C12.4434 7.48242 6.35102 10 -0.000296474 10L39.9019 10C33.5506 10 27.4583 7.48242 22.9597 2.99887L19.9508 0Z" fill="white"/>
          </svg>
        </div>
        <div class="onboarding">
          <div>
            <p>{{ $t('Каждая языковая версия страницы настраивается отдельно.') }}</p>
            <p>{{ $t('Рекомендуем сначала заполнить страницу на одном языке, а потом перевести её на другие языки') }}</p>
          </div>
          <v-btn variant="flat" color="primary" rounded="lg" class="w-100" @click="sentHint">
            {{ $t('Понятно') }}
          </v-btn>
        </div>
      </v-menu>
    </div>
  </div>

  <v-overlay
      :model-value="showOnboarding"
      :scrim="true"
      opacity="0.7"
      persistent
      style="z-index: 2000"
  />
</template>


<script setup lang="ts">
import BackNewIcon from '~/assets/icons/back-new.vue'
import ChevronDownNewIcon from '~/assets/icons/chevron-down-new.vue'
import UnionIcon from '~/assets/icons/union.vue'
import { useTapStore } from '@/stores/tapStore'
import {computed, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useHintStore} from "@/stores/hintStore";
import {useDialogStore} from "@/stores/dialogStore.js";
import Context from "@/components/dialogs/Context.vue";
import InnerTapList from "@/components/dialogs/InnerTapList.vue";

const tapStore = useTapStore()

defineProps<{
  name: string
}>()

const route = useRoute()
const router = useRouter()
const hintStore = useHintStore()
const dialogStore = useDialogStore()
const isEditMode = computed(() => route.params.mode === 'edit')

const languages = ref([])
const onboarding = ref(false)

const showOnboarding = computed(() => {
  return !!onboarding.value && hintStore.canShow('language_versions')
})

const openInnerTapList = () => {
  dialogStore.open(InnerTapList)
}

const openContext = () => {
  const listedTap = tapStore.tapList.find(tap => tap.id === tapStore.currentTap.id)

  dialogStore.open(Context, {
    id: tapStore.currentTap.id,
    alias: tapStore.currentTap.alias,
    name: tapStore.currentTapLang.name,
    image: tapStore.currentTap.image,
    lang: tapStore.currentTapLang.lang.toLowerCase(),
    published: tapStore.currentTap.status.id == 3,
    defaultLang: tapStore.currentTap.projectVersion.defaultLang.toLowerCase(),
    searchable: tapStore.currentTap.searchable ?? tapStore.currentTap.isSearchable ?? tapStore.currentTap.is_searchable ?? listedTap?.searchable ?? true
  })
}

const sentHint = () => {
  onboarding.value = false
  hintStore.closeHint('language_versions')
}

const setLang = (lang) => {
  const basePath = route.path.replace(/\/(ru|en|kz)\/?$/, '')
  const params = new URLSearchParams(route.query).toString()
  const queryWithQuestionMark = params ? `?${params}` : ''
  router.push(`${basePath}/${lang.toLowerCase()}${queryWithQuestionMark}`)
}

watch(() => tapStore.languageSettings,(newValue) => {
  if(newValue) {
    languages.value = tapStore.languageSettings.filter(l => l.active || l.edited)
    checkAndShowHint()
  }
})

if(tapStore.languageSettings) {
  languages.value = tapStore.languageSettings.filter(l => l.active || l.edited)
}
onMounted(() => {
  if(!languages.value) {
    languages.value = tapStore.languageSettings.filter(l => l.active || l.edited)
  }
})

const checkAndShowHint = () => {
  if(languages.value?.length <= 1) {
    return
  }
  hintStore.checkHint('language_versions').then(result => {
    onboarding.value = !result
    if(onboarding.value) {
      hintStore.viewHint('language_versions')
    }
  })
}
</script>

<style scoped lang="sass">

.highlighted-element
  z-index: 2100 !important
  background-color: white !important
  position: relative
  border-radius: 10px
  overflow: hidden

.onboarding
  padding: 20px
  background: #fff
  display: flex
  justify-content: center
  flex-direction: column
  gap: 16px
  border-radius: 10px
  max-width: 100%
  color: #363636
  text-align: center
  font-size: 16px
  font-weight: 400
  line-height: 130%

.navigation
  background: #fff
  border-radius: 0 0 16px 16px
  min-height: 56px
  box-sizing: border-box
  padding: 8px 16px
  gap: 8px
  &.no-round
    border-radius: 0

  &__back,
  &__spacer
    width: 24px
    height: 24px
    flex: 0 0 24px

  &__back,
  &__menu
    display: flex
    align-items: center
    justify-content: center
    border-radius: 50%

    &:hover
      background: #E7F3FD

  &__menu
    width: 40px
    height: 40px
    flex: 0 0 40px
    padding: 8px
    border: 0
    background: transparent
    cursor: pointer
    margin-left: auto

  .tap-selector
    min-width: 0
    height: 24px
    box-sizing: border-box
    flex: 0 1 auto
    display: flex
    align-items: center
    gap: 4px
    padding: 3px 8px
    border: 0
    border-radius: 8px
    background: transparent
    cursor: pointer

    &:hover
      background: #E7F3FD

    &__icon
      flex: 0 0 24px

  .tap-name
    min-width: 0
    flex: 1 1 auto
    color: #363636
    font-size: 16px
    font-weight: 600
    line-height: normal
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
.languages
  background: #fff
  .tab
    color: #363636
    text-align: center
    font-size: 16px
    font-style: normal
    font-weight: 400
    line-height: 24px
    position: relative
    flex: 1
    cursor: pointer
    user-select: none
    &.active
      font-weight: 500
      &:after
        content: ''
        height: 2px
        width: 100%
        position: absolute
        bottom: 0
        left: 0
        border-radius: 4px 4px 0 0
        background: #0D87EF
</style>
